import React, { useEffect, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageTitle } from 'theme/styles';
import {
  EArtAmmunitionType,
  EArtAncillaryTypeName,
  EArtComponentType,
} from 'constants/ArtAccessoryType';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes, ETransactionDictionaryItemTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ERouteLinks } from 'models/route';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { Stack, Typography } from '@mui/material';
import {
  getFieldsByAccessoriesType,
  getFieldsByAmmunitionType,
  getFieldsByComponentType,
} from 'pages/AncillariesPage/helpers';
import { colors } from 'theme/colors';
import {
  EAccessoryLabels,
  EAmmunitionLabels,
  EComponentLabels,
} from 'pages/AncillariesPage/AncillariesReferenceTablePage/DetailAncillariesReference';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EPermission } from 'models/permissions';
import { IARTAccessories, IARTAmmunition, IARTComponent } from 'models/ancillaries';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { findInDictionary } from 'helpers/dictionary';

interface IArtDetails {
  artAccessoryDetail?: IARTAccessories | null;
  artAmmunitionDetail?: IARTAmmunition | null;
  artComponentDetail?: IARTComponent | null;
  type: string;
}

const AuditLogArtDetails = ({
  artAccessoryDetail,
  artAmmunitionDetail,
  artComponentDetail,
  type,
}: IArtDetails) => {
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();
  const [fields, setFields] = useState<string[] | undefined>(undefined);
  const transactionPagePath = useMatch(ERouteLinks.LegalEntityViewAncillary);
  const isTransactionPage = !!transactionPagePath;
  const ancillaryDetails = useMatch(ERouteLinks.ViewAncillaryDetails);

  const { loadDictionaries, firearmType, make, model, manufacturer } = useAncillaries();

  useEffect(() => {
    if (artAccessoryDetail?.accessoryTypeUniqueId) {
      setFields(getFieldsByAccessoriesType(artAccessoryDetail?.accessoryTypeUniqueId));
    } else if (artAmmunitionDetail?.ammunitionTypeUniqueId) {
      setFields(
        getFieldsByAmmunitionType(
          artAmmunitionDetail?.ammunitionTypeUniqueId as EArtAmmunitionType,
        ),
      );
    } else if (artComponentDetail?.componentTypeUniqueId) {
      setFields(
        getFieldsByComponentType(
          artComponentDetail?.componentTypeUniqueId as EArtComponentType,
        ),
      );
    }
  }, [artAccessoryDetail, artAmmunitionDetail, artComponentDetail]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.ArtAncillaryOptions]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <div>
      {type === EArtAncillaryTypeName.ACCESSORY.toLowerCase() && (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {artAccessoryDetail && fields && (
            <Grid sx={{ gap: 3 }}>
              <DetailsTemplate
                deletePermissions={[EPermission.MANAGE_FRT_FIREARM]}
                title={artAccessoryDetail?.accessoryType}
                pageType={
                  isTransactionPage
                    ? EDetailsPageTypes.transactionSelectView
                    : EDetailsPageTypes.view
                }
                redirectToView={
                  !ancillaryDetails
                    ? () => {
                        navigate(
                          ERouteLinks.ViewAncillaries.replace(
                            ':id',
                            artAccessoryDetail.uniqueId,
                          ).replace(':type', EArtAncillaryTypeName.ACCESSORY.toLowerCase()),
                        );
                      }
                    : undefined
                }
                actionButtonProps={{
                  ...{ label: t('legalEntityDetails.ancillary.accordion.details.link') },
                  ...(isDesktop ? {} : { size: EButtonSizes.small }),
                  ...(isDesktop ? {} : { sizePX: EButtonSizesPX.px24 }),
                  ...{ variant: EButtonVariants.outlined },
                }}
                disableActions
              >
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.ancillary)}
                    </Typography>
                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                      {ETransactionDictionaryItemTypes.ACCESSORY.toLocaleLowerCase()}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.accessoryType)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artAccessoryDetail?.accessoryType)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.artId)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artAccessoryDetail?.artId)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAccessoryLabels.manufacturer)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artAccessoryDetail?.manufacturer)}
                    </Typography>
                  </Stack>
                </Grid>

                {fields.includes('hostTypeUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.hostType)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.hostType)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('calibreUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.calibre)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.calibre)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('compositionUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.composition)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.composition)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('proofHouseUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.proofHouse)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.proofHouse)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('lightColourUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.lightColour)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.lightColour)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('capacity') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.capacity)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.capacity)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('magnificationUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.magnification)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.magnification)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('utilityUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.utility)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.utility)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('rectileUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.rectile)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.rectile)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('productName') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.productName)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAccessoryDetail?.productName)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
              </DetailsTemplate>
            </Grid>
          )}
        </>
      )}

      {type === EArtAncillaryTypeName.AMMUNITION.toLowerCase() && (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {artAmmunitionDetail && fields && (
            <Grid sx={{ gap: 3 }}>
              <DetailsTemplate
                deletePermissions={[EPermission.MANAGE_FRT_FIREARM]}
                title={artAmmunitionDetail?.ammunitionType}
                pageType={
                  isTransactionPage
                    ? EDetailsPageTypes.transactionSelectView
                    : EDetailsPageTypes.view
                }
                redirectToView={
                  !ancillaryDetails
                    ? () => {
                        navigate(
                          ERouteLinks.ViewAncillaries.replace(
                            ':id',
                            artAmmunitionDetail.uniqueId,
                          ).replace(':type', EArtAncillaryTypeName.AMMUNITION.toLowerCase()),
                        );
                      }
                    : undefined
                }
                actionButtonProps={{
                  ...{ label: t('legalEntityDetails.ancillary.accordion.details.link') },
                  ...(isDesktop ? {} : { size: EButtonSizes.small }),
                  ...(isDesktop ? {} : { sizePX: EButtonSizesPX.px24 }),
                  ...{ variant: EButtonVariants.outlined },
                }}
                disableActions
              >
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.ancillary)}
                    </Typography>
                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                      {ETransactionDictionaryItemTypes.AMMUNITION.toLocaleLowerCase()}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.ammunitionType)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artAmmunitionDetail?.ammunitionType)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.artId)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artAmmunitionDetail?.artId)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.manufacturer)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artAmmunitionDetail?.manufacturer)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EAmmunitionLabels.factory)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artAmmunitionDetail?.factory)}
                    </Typography>
                  </Stack>
                </Grid>
                {fields.includes('calibreUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.calibre)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.calibre)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('cartridgeCompositionUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.cartridgeComposition)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.cartridgeComposition)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('colourOfPrimerSealantUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.colourOfPrimerSealant)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.colourOfPrimerSealant)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('projectileShapeUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.projectileShape)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.projectileShape)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('functionalTypeUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.functionalType)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.functionalType)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('shotSizeUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.shotSize)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.shotSize)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('munitionTypeUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.munitionTypeUniqueId)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.munitionType)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('munitionVelocityUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.munitionVelocity)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.munitionVelocity)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('headstampYear') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.headstampYear)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.headstampYear)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('alphanumericalMarkings') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.alphanumericalMarkings)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.alphanumericalMarkings)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('productName') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAmmunitionLabels.productName)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artAmmunitionDetail?.productName)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
              </DetailsTemplate>
            </Grid>
          )}
        </>
      )}

      {type === EArtAncillaryTypeName.COMPONENTS.toLowerCase() && (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {artComponentDetail && fields && (
            <Grid sx={{ gap: 3 }}>
              <DetailsTemplate
                deletePermissions={[EPermission.MANAGE_FRT_FIREARM]}
                title={artComponentDetail?.componentType}
                pageType={
                  isTransactionPage
                    ? EDetailsPageTypes.transactionSelectView
                    : EDetailsPageTypes.view
                }
                redirectToView={
                  !ancillaryDetails
                    ? () => {
                        navigate(
                          ERouteLinks.ViewAncillaries.replace(
                            ':id',
                            artComponentDetail.uniqueId,
                          ).replace(':type', EArtAncillaryTypeName.COMPONENTS.toLowerCase()),
                        );
                      }
                    : undefined
                }
                actionButtonProps={{
                  ...{ label: t('legalEntityDetails.ancillary.accordion.details.link') },
                  ...(isDesktop ? {} : { size: EButtonSizes.small }),
                  ...(isDesktop ? {} : { sizePX: EButtonSizesPX.px24 }),
                  ...{ variant: EButtonVariants.outlined },
                }}
                disableActions
              >
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.ancillary)}
                    </Typography>
                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                      {ETransactionDictionaryItemTypes.COMPONENT.toLocaleLowerCase()}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.componentType)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artComponentDetail?.componentType)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.artId)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artComponentDetail?.artId)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.manufacturer)}
                    </Typography>
                    <Typography variant="body2" style={{}}>
                      {emptyFieldHolder(artComponentDetail?.manufacturer)}
                    </Typography>
                  </Stack>
                </Grid>
                {fields.includes('calibreUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.calibre)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artComponentDetail?.calibre)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('length') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.length)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artComponentDetail?.length)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('compositionUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.composition)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artComponentDetail?.composition)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('proofHouseUniqueId') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.proofHouse)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artComponentDetail?.proofHouse)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {fields.includes('productName') && (
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.productName)}
                      </Typography>
                      <Typography variant="body2" style={{}}>
                        {emptyFieldHolder(artComponentDetail?.productName)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}

                <Grid
                  container
                  sx={{
                    borderTop: `1px solid ${colors.primary100}`,
                    marginTop: '30px',
                    paddingTop: '25px',
                  }}
                >
                  <Stack>
                    <StyledPageTitle variant="h5" sx={{ marginBottom: '20px' }}>
                      {t('ancillaries.accordion.firearmInformation.label')}
                    </StyledPageTitle>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.frtTypeUniqueId)}
                    </Typography>
                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                      {artComponentDetail?.frtTypeUniqueId
                        ? findInDictionary(artComponentDetail?.frtTypeUniqueId, firearmType)
                        : emptyFieldHolder(artComponentDetail?.frtType)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.frtManufacturerUniqueId)}
                    </Typography>
                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                      {emptyFieldHolder(artComponentDetail?.frtManufacturerUniqueId)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.frtMakeUniqueId)}
                    </Typography>
                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                      {emptyFieldHolder(artComponentDetail?.frtMakeUniqueId)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.frtModelUniqueId)}
                    </Typography>
                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                      {emptyFieldHolder(artComponentDetail?.frtModelUniqueId)}
                    </Typography>
                  </Stack>
                </Grid>
              </DetailsTemplate>
            </Grid>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogArtDetails;
